import { useEffect, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import type { HDEExistingSubmission } from '~/routes/remix-api.app.hde-existing-submission';
import { HDE_EXISTING_SUBMISSION_API_ROUTE } from '~/routes/remix-api.app.hde-existing-submission';

interface ExistingSubmissionResponse {
    eventId?: number;
    submission: HDEExistingSubmission | null;
    isEventGuest?: boolean;
    error?: string;
}

interface Resolved {
    eventId: number;
    submission: HDEExistingSubmission | null;
    isEventGuest: boolean;
}

/**
 * Fetches the current member's existing HDE submission for an event so the modal can
 * offer to update it (prefill / application-type switch).
 *
 * Fires when `enabled` (authenticated) and a positive `eventId` is known. `isChecking`
 * is true from the moment the lookup is enabled until a response has resolved for that
 * event — letting the modal show a "checking your registration" view instead of briefly
 * rendering the first step and then jumping to the gate.
 */
export function useHdeExistingSubmission(
    eventId: number | null,
    enabled: boolean,
    /** Authenticated member id — scopes fetcher cache and invalidates on account change. */
    memberKey: string | null = null,
) {
    const fetcher = useFetcher<ExistingSubmissionResponse>({
        key: `hde-existing-submission:${memberKey ?? 'anonymous'}`,
    });
    const loadRef = useRef(fetcher.load);
    loadRef.current = fetcher.load;
    const lastRequestKeyRef = useRef<string | null>(null);
    const [resolved, setResolved] = useState<Resolved | null>(null);

    const active =
        enabled && Boolean(memberKey) && Boolean(eventId) && (eventId ?? 0) > 0;

    useEffect(() => {
        if (!enabled || !memberKey) {
            lastRequestKeyRef.current = null;
            setResolved(null);
        }
    }, [enabled, memberKey]);

    useEffect(() => {
        if (!active || !eventId || !memberKey) {
            return;
        }
        const requestKey = `${memberKey}:${eventId}`;
        if (lastRequestKeyRef.current === requestKey) {
            return;
        }
        lastRequestKeyRef.current = requestKey;
        setResolved(null);

        const params = new URLSearchParams({ eventId: String(eventId) });
        loadRef.current(
            `${HDE_EXISTING_SUBMISSION_API_ROUTE}?${params.toString()}`,
        );
    }, [active, eventId, memberKey]);

    // Capture the response once the request for the current event settles.
    useEffect(() => {
        if (
            eventId &&
            memberKey &&
            lastRequestKeyRef.current === `${memberKey}:${eventId}` &&
            fetcher.state === 'idle' &&
            fetcher.data !== undefined &&
            fetcher.data.eventId === eventId
        ) {
            setResolved({
                eventId,
                submission: fetcher.data.submission ?? null,
                isEventGuest: fetcher.data.isEventGuest ?? false,
            });
        }
    }, [eventId, memberKey, fetcher.state, fetcher.data]);

    const isResolvedForEvent =
        Boolean(eventId) && resolved?.eventId === eventId;
    const existingSubmission = isResolvedForEvent
        ? (resolved?.submission ?? null)
        : null;
    const isEventGuest = isResolvedForEvent
        ? (resolved?.isEventGuest ?? false)
        : false;
    const isChecking = active && !isResolvedForEvent;

    return {
        existingSubmission,
        isEventGuest,
        isChecking,
    };
}
